import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { TimeField } from '@/components/form/TimeInput';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { DayKey, DefaultShiftTimes } from '@/services/settings/systemSettings.types';
import { useSystemSettings, useUpdateSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GiCog } from 'react-icons/gi';
import adminStrings from '../admin.translations';

const AdminGeneralSection: FunctionComponent = () => {
  const [isEditing, setIsEditing] = useState(false);
  const defaultShiftTimes = useSystemSettings(s => s.settings.defaultShiftTimes);
  const { updateSettings, fetching } = useUpdateSystemSettings();

  const methods = useForm({ defaultValues: defaultShiftTimes });

  const onSubmit = async (values: DefaultShiftTimes) => {
    const success = await updateSettings({ defaultShiftTimes: values });
    if (success) {
      setIsEditing(false);
    }
  };

  return (
    <TSTitleCard
      title={adminStrings.generalSection.title}
      icon={GiCog}
      className="w-full"
      primaryAction={!isEditing ? { label: strings.common.edit, onClick: () => setIsEditing(true) } : undefined}
    >
      <div className="space-y-5">
        <div className="space-y-1">
          <p className="text-xl font-semibold">{adminStrings.generalSection.defaultShiftTimes}</p>
          <div className="leading-tight">
            <p className="text-secondary-std">{adminStrings.generalSection.defaultShiftTimesDescription}</p>
            <p className="text-secondary-std">{adminStrings.generalSection.defaultShiftTimesDescriptionPart2}</p>
          </div>
        </div>

        {isEditing ? (
          <SubmissionErrorBoundary>
            <TsForm methods={methods} onSubmit={onSubmit}>
              <div className="md:ml-20 w-full flex flex-col space-y-3">
                <div className="flex flex-row items-center gap-2">
                  <div className="w-40" />
                  <p className="w-40 text-center font-medium">{adminStrings.generalSection.startTime}</p>
                  <p className="w-40 text-center font-medium">{adminStrings.generalSection.endTime}</p>
                </div>
                {strings.common.daysOfWeekFullStartMonday.map(day => {
                  const key = day.toLowerCase() as DayKey;
                  return (
                    <div key={key} className="flex flex-row items-center space-x-3">
                      <p className="w-40 font-medium">{day}</p>
                      <TimeField className="w-40" id={`${key}-start`} name={`${key}.start`} />
                      <TimeField className="w-40" id={`${key}-end`} name={`${key}.end`} />
                    </div>
                  );
                })}
              </div>
              <div className="flex w-full items-center justify-end gap-5 mt-5">
                <Button onClick={() => setIsEditing(false)}>{strings.common.cancel}</Button>
                <Button primary loading={fetching} type="submit">
                  {strings.common.save}
                </Button>
              </div>
            </TsForm>
          </SubmissionErrorBoundary>
        ) : (
          <div className="w-full flex flex-col space-y-3 md:ml-20">
            {strings.common.daysOfWeekFullStartMonday.map(day => {
              const key = day.toLowerCase() as DayKey;
              return (
                <div key={key} className="flex flex-row items-center space-x-10">
                  <p className="w-20 mr-10 font-medium">{day}</p>
                  <p>
                    {defaultShiftTimes[key].start} - {defaultShiftTimes[key].end}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TSTitleCard>
  );
};

export default AdminGeneralSection;
